import { CSSProperties, memo, useCallback, useMemo } from 'react';
import { ColumnRowProps } from '../../ColumnRowProps';
import { useGridRowContext } from '../../../../../../../_grid/contexts/rowContext';
import { useScrollToContext } from '../../../../context/ScrollToContext';
import { Stack } from '@fluentui/react';
import { GridCell } from '../../../../../../../../../api/generated/data-contracts';
import { useGridContext } from '../../../../../../../_grid/contexts/gridContext';
import { useFixedCellValue } from '../../../../../../../_grid/hooks/useFixedCellValue';
import { CELL_HEIGHT, CELL_WIDTH } from '../../../../CONSTANTS';
import { ColumnGridContainer } from '../ColumnGridContainer';
import { Cell } from '../../../Cell/Cell';
import { useTheme } from '../../../../../../../../../Themes/themeContext';
import { useGetDecimalCellValue } from '../../../../../../../projectplanner/components/Grid/hooks/useGetDecimalCellValue';
import { ReadOnlyCell } from './ReadOnlyCell';

export const ResourceColumnRow = memo(function ResourceColumnRow({ columnRow, expanded, columnIndex, rowId }: ColumnRowProps & { expanded: boolean }) {
    const { toggleExpanded, rowIndex } = useGridRowContext();
    const render = useMemo(() => {
        return columnRow.cells.map((cell, i) => {
            if (!cell) {
                return <div key={i} style={{ width: CELL_WIDTH }}></div>;
            }
            if (i === 1) { // Allocation column
                return <Cell key={cell.id} cell={cell} cellIndex={i} columnIndex={columnIndex} rowId={rowId} />;
            }
            if (i === 2) { // Free cap column
                return <FreeCapCell key={cell.id} cell={cell} />;
            }
            return <ReadOnlyCell key={cell.id} cell={cell} />;
        });
    }, [columnIndex, columnRow.cells, rowId]);

    const scrollTo = useScrollToContext();
    const onClick = useCallback(() => {
        if (!expanded) {
            scrollTo({ rowIndex: rowIndex - 1, align: 'start' });
        }
        toggleExpanded();
    }, [expanded, rowIndex, scrollTo, toggleExpanded]);

    return (
        <Stack onClick={onClick} style={containerStyles}>
            <ColumnGridContainer>{render}</ColumnGridContainer>
        </Stack>
    );
});

const containerStyles: CSSProperties = { height: '100%' };

const FreeCapCell = ({ cell, className }: { cell: GridCell; className?: string }) => {
    const { useGridCellValue } = useGridContext();
    const storeValue = useGridCellValue(cell);
    const value = useFixedCellValue(storeValue);
    const backgroundColor = useFreeCapValueBgColor(storeValue.toNumber());

    return (
        <div
            style={{
                paddingRight: 8,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'end',
                backgroundColor,
                height: CELL_HEIGHT,
                width: CELL_WIDTH,
            }}
            className={className}
        >
            {value}
        </div>
    );
};

const useFreeCapValueBgColor = (allocationValue: number) => {
    const theme = useTheme();
    const getDecimals = useGetDecimalCellValue();
    const allocationDecimalValue = useMemo(() => Number(getDecimals(allocationValue)), [getDecimals, allocationValue]);
    return useMemo(() => {
        if (allocationDecimalValue < 0) {
            return theme.semanticColors.capacityOverBackground;
        } else if (allocationDecimalValue > 0) {
            return theme.semanticColors.capacityUnderBackground;
        } else if (allocationDecimalValue === 0) {
            return theme.semanticColors.capacityEqualBackground;
        }
    }, [
        allocationDecimalValue,
        theme.semanticColors.capacityOverBackground,
        theme.semanticColors.capacityUnderBackground,
        theme.semanticColors.capacityEqualBackground,
    ]);
};
